import {
  classificarResposta,
  formatarDataPayload,
  parseData,
} from '../EstruturaChecklist/checklist.util'

export function respostasVazias() {
  return { fields: {}, items: {}, observations: null }
}

export function respondido(valor) {
  return valor !== null && valor !== undefined && valor !== ''
}

export function camposDaEstrutura(estrutura) {
  return [...(estrutura.header || []), ...(estrutura.footer || [])]
}

export function itensDaEstrutura(estrutura) {
  const itens = []
  ;(estrutura.body || []).forEach(etapa =>
    etapa.groups.forEach(grupo => grupo.items.forEach(item => itens.push(item))),
  )
  return itens
}

export function parseRespostas(doc, estrutura) {
  const origem = doc && typeof doc === 'object' ? doc : {}
  const fields = { ...(origem.fields || {}) }
  const items = {}

  Object.keys(origem.items || {}).forEach(id => {
    const item = origem.items[id] || {}
    items[id] = {
      value: item.value === undefined ? null : item.value,
      observation: item.observation || null,
    }
  })

  camposDaEstrutura(estrutura).forEach(campo => {
    if (campo.field_type === 'date' && typeof fields[campo.id] === 'string') {
      fields[campo.id] = parseData(fields[campo.id])
    }
  })
  itensDaEstrutura(estrutura).forEach(item => {
    const resposta = items[item.id]
    if (item.field_type === 'date' && resposta && typeof resposta.value === 'string') {
      items[item.id] = { ...resposta, value: parseData(resposta.value) }
    }
  })

  return { fields, items, observations: origem.observations || null }
}

function valorPayload(tipo, valor) {
  if (!respondido(valor)) return null
  if (tipo === 'date') return formatarDataPayload(valor)
  if (tipo === 'checkbox') return valor === true || valor === false ? valor : null
  if (typeof valor === 'string') return valor.trim() || null
  return valor
}

function textoPayload(valor) {
  return typeof valor === 'string' && valor.trim() ? valor.trim() : null
}

export function montarPayloadRespostas(respostas, estrutura) {
  const fields = {}
  camposDaEstrutura(estrutura).forEach(campo => {
    const valor = valorPayload(campo.field_type, respostas.fields[campo.id])
    if (valor !== null) fields[campo.id] = valor
  })

  const items = {}
  itensDaEstrutura(estrutura).forEach(item => {
    const resposta = respostas.items[item.id]
    if (!resposta) return

    const valor = valorPayload(item.field_type, resposta.value)
    const observation = textoPayload(resposta.observation)
    if (valor !== null || observation) items[item.id] = { value: valor, observation }
  })

  return { fields, items, observations: textoPayload(respostas.observations) }
}

export function listarPendencias(estrutura, respostas) {
  const payload = montarPayloadRespostas(respostas, estrutura)
  const pendencias = []

  const conferirCampos = (campos, secao) =>
    campos.forEach(campo => {
      if (!respondido(payload.fields[campo.id])) {
        pendencias.push({ id: campo.id, path: `${secao} > ${campo.label}` })
      }
    })

  conferirCampos(estrutura.header || [], 'Cabeçalho')
  ;(estrutura.body || []).forEach(etapa =>
    etapa.groups.forEach(grupo =>
      grupo.items.forEach((item, indice) => {
        const resposta = payload.items[item.id]
        if (!resposta || !respondido(resposta.value)) {
          pendencias.push({
            id: item.id,
            path: `${etapa.step_name} > ${grupo.group_name} > Item ${indice + 1}`,
          })
        }
      }),
    ),
  )
  conferirCampos(estrutura.footer || [], 'Rodapé')

  return pendencias
}

export function contarRespondidos(estrutura, respostas) {
  const payload = montarPayloadRespostas(respostas, estrutura)
  const campos = camposDaEstrutura(estrutura)
  const itens = itensDaEstrutura(estrutura)

  const camposRespondidos = campos.filter(campo => respondido(payload.fields[campo.id])).length
  const itensRespondidos = itens.filter(
    item => payload.items[item.id] && respondido(payload.items[item.id].value),
  ).length

  return { respondidos: camposRespondidos + itensRespondidos, total: campos.length + itens.length }
}

export function progressoEtapa(etapa, respostas) {
  let respondidos = 0
  let total = 0

  etapa.groups.forEach(grupo =>
    grupo.items.forEach(item => {
      total++
      const resposta = respostas.items[item.id]
      if (resposta && respondido(resposta.value)) respondidos++
    }),
  )

  return { respondidos, total }
}

export function calcularResultado(estrutura, respostas) {
  const payload = montarPayloadRespostas(respostas, estrutura)
  const resultado = {
    success: 0,
    fail: 0,
    neutral: 0,
    informational: 0,
    scored: 0,
    answered: 0,
    total: 0,
    percentual: null,
  }

  itensDaEstrutura(estrutura).forEach(item => {
    const resposta = payload.items[item.id]
    const valor = resposta ? resposta.value : null

    resultado.total++
    if (respondido(valor)) resultado.answered++

    if (item.field_type !== 'dropdown' && item.field_type !== 'checkbox') {
      resultado.informational++
      return
    }

    resultado.scored++
    const classificacao = classificarResposta(item, valor)
    if (classificacao) resultado[classificacao]++
  })

  const base = resultado.success + resultado.fail
  resultado.percentual = base ? Math.round((resultado.success / base) * 1000) / 10 : null

  return resultado
}

export function formatarPercentual(percentual) {
  if (percentual === null || percentual === undefined) return 'Sem itens pontuados'
  return `${String(percentual).replace('.', ',')}%`
}

// 'YYYY-MM-DD HH:mm[:ss]' (sem fuso) é interpretado como horário local; ISO com fuso vai pelo Date
export function parseDataHora(texto) {
  if (!texto) return null
  if (texto instanceof Date) return texto

  const partes = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})(?::(\d{2}))?$/.exec(String(texto))
  if (partes) {
    const [ano, mes, dia, hora, minuto] = partes.slice(1, 6).map(Number)
    return new Date(ano, mes - 1, dia, hora, minuto)
  }

  const data = new Date(texto)
  return isNaN(data.getTime()) ? null : data
}

export function formatarDataHora(texto) {
  const data = parseDataHora(texto)
  if (!data) return ''

  const pad = valor => `${valor}`.padStart(2, '0')
  return `${pad(data.getDate())}/${pad(data.getMonth() + 1)}/${data.getFullYear()} ${pad(data.getHours())}:${pad(data.getMinutes())}`
}

export function formatarDataHoraPayload(data, hora) {
  const dia = formatarDataPayload(data)
  if (!dia || !/^\d{4}-\d{2}-\d{2}$/.test(dia)) return null

  const partes = /^(\d{2}):(\d{2})$/.exec(hora || '')
  if (!partes || Number(partes[1]) > 23 || Number(partes[2]) > 59) return null

  return `${dia} ${partes[1]}:${partes[2]}`
}
